import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// Validate URL format
try {
  new URL(supabaseUrl);
} catch (e) {
  throw new Error('Invalid Supabase URL format. Please check your VITE_SUPABASE_URL environment variable.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

export async function checkConnection() {
  try {
    // First check if we can reach the Supabase service
    const headers = {
      'apikey': supabaseAnonKey,
      'Content-Type': 'application/json'
    };
    
    const healthCheck = await fetch(`${supabaseUrl}/rest/v1/`, { headers }).catch(() => null);
    if (!healthCheck) {
      throw new Error('Unable to reach Supabase service. Please check your internet connection.');
    }

    // Then try to make an authenticated request
    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .limit(1);
    
    if (error) {
      if (error.code === 'PGRST301') {
        throw new Error('Invalid API key. Please check your VITE_SUPABASE_ANON_KEY environment variable.');
      }
      throw error;
    }
    
    return { success: true, error: null };
  } catch (error) {
    console.error('Failed to connect to Supabase:', error);
    const errorMessage = error instanceof Error 
      ? error.message
      : 'Failed to connect to Supabase. Please check your connection and credentials.';
    
    return { 
      success: false, 
      error: new Error(errorMessage)
    };
  }
}

// Helper function to get user profile with Google info
export async function getUserProfile() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { data: null, error: new Error('No user found') };
    }
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
      
    if (error) throw error;
    
    // Enhance profile with OAuth provider info
    const enhancedProfile = {
      ...data,
      provider: user.app_metadata.provider,
      avatar_url: user.user_metadata.avatar_url,
      full_name: user.user_metadata.full_name
    };
    
    return { data: enhancedProfile, error: null };
  } catch (error) {
    console.error('Error getting user profile:', error);
    return { 
      data: null, 
      error: error instanceof Error ? error : new Error('Failed to get user profile') 
    };
  }
}

// Function to check if the browser supports local storage
export function supportsLocalStorage(): boolean {
  try {
    const testKey = '__test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

// Function to check if the browser supports IndexedDB
export function supportsIndexedDB(): boolean {
  try {
    return !!window.indexedDB;
  } catch (e) {
    return false;
  }
}

// Function to check browser capabilities
export function getBrowserCapabilities() {
  return {
    localStorage: supportsLocalStorage(),
    indexedDB: supportsIndexedDB(),
    serviceWorker: 'serviceWorker' in navigator,
    onLine: navigator.onLine
  };
}